import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ILegalEntity, Order } from '@cemex-core/cmx-api';
import { BusinessLine } from '@cemex-core/cmx-api/models/business-lines/business-line';
import { ShippingCondition } from '@cemex-core/cmx-api/models/shipping-conditions/shipping-condition';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { select, Store } from '@ngrx/store';
import {
  OneClickLoadBusinessLinesAction,
  OneClickSelectBusinessLinesAction,
} from '@ordersnproducts/modules/one-click-order/actions/one-click-business-lines.actions';
import { getOneClickBusinessLines } from '@ordersnproducts/modules/one-click-order/selectors/one-click-business-lines.selector';
import { DynamicEffectsRegistratorService } from '@ordersnproducts/modules/shared/modules/dynamic-effects/dynamic-effects-registrator.service';
import { RtlDetectorService } from '@ordersnproducts/modules/shared/services';
import { runJSQueue } from '@ordersnproducts/utils/run-js-queue';
import { Subject } from 'rxjs';
import { debounceTime, filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { Features } from '@ordersnproducts/features';
import { OrderCreationIcon } from '../../../order-builder/models/order-strategy-response';
import { ConfigurableFormComponent } from '../../../shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import {
  OneClickCreateProfileAction,
  OneClickProfileResetAction,
  OneClickProfileResetBuilderAction,
  OneClickUpdateProfileAction,
} from '../../actions/one-click-profile.actions';
import {
  OneClickLoadShippingModeCompleteAction,
  OneClickSelectShippingModeAction,
} from '../../actions/one-click-shipment-mode.actions';
import {
  OneClickLoadShippingMethodAction,
  OneClickSelectShippingMethodAction,
} from '../../actions/one-click-shipping-method.actions';
import { OneClickProfilesLoad } from '../../actions/one-click-table-profiles.actions';
import { OneClickJobsitesEffects } from '../../effects/one-click-jobsites.effects';
import { OneClickPointOfDeliveriesEffects } from '../../effects/one-click-pods.effects';
import { isJobsite, isPointOfDelivery } from '../../helpers/utilities';
import { ShippingLocationMode, ShippingLocationModeCode } from '../../models/shipping-location-mode.model';
import { State } from '../../reducers';
import { OneClickProfileState } from '../../reducers/one-click-profile.reducer';
import { getOneClickIsSelectedOrderFromTable } from '../../selectors/one-click-orders.selector';
import {
  getOneClickPreloadedOrder,
  getOneClickPreloadedOrderLoading,
  getOneClickProfileState,
} from '../../selectors/one-click-profiles.selector';
import { getOneClickShippingMethods } from '../../selectors/one-click-shipping-methods.selector';
import { OneClickConfigLoaderService } from '../../services/one-click-config-loader.service';
import { OneClickBuilderField } from '../../util/constants/builder-fields';
import { FieldConfig } from './../../../shared/modules/configurable-form/models/field-configs/field-config.interface';
import { getOneClickStdLot } from './../../selectors/one-click-jobsites.selector';
import { getOneClickBuilderEditMode } from './../../selectors/one-click-profiles.selector';
import { OrderFieldTestIds } from '@ordersnproducts/modules/order-field-test-ids-enum';

@Component({
  selector: 'app-one-click-template-builder',
  templateUrl: './one-click-template-builder.component.html',
  styleUrls: ['./one-click-template-builder.component.scss', './custom-fields.scss', './order-placed-modal.scss'],
})
export class OneClickTemplateBuilderComponent implements OnInit, OnDestroy {
  @ViewChild('builder', { read: ViewContainerRef }) entry: ViewContainerRef;
  public componentRenders: ComponentRef<ConfigurableFormComponent>[] = [];
  public OrderCreationIcon = OrderCreationIcon;
  public config: FieldConfig[];
  public shippingMethods: ShippingCondition[];
  public businessLines: BusinessLine[];
  public profileState: OneClickProfileState;
  public stdLoading = false;
  public shippingLocationModes: ShippingLocationMode[] = [];
  public selectedShippingMode: ShippingLocationMode;
  public preloadedOrder: Order;
  public isLoadingPreloadedOrder: boolean;
  public isEditMode: boolean;
  public canAddNewProduct: boolean;
  public rtl = false;
  public isFormDataValidIndicator = false;
  public preSelectedBusinessLine: BusinessLine;
  public orderFieldTestIds = OrderFieldTestIds;
  private ngDestroy = new Subject();
  private formRebuild = new Subject();

  constructor(
    public rtlDetector: RtlDetectorService,
    private configService: OneClickConfigLoaderService,
    private store: Store<State>,
    private dialogRef: MatDialogRef<MatDialog>,
    private router: Router,
    private resolver: ComponentFactoryResolver,
    private featureToggle: FeatureToggleService,
    private dynamicEffectsService: DynamicEffectsRegistratorService,
  ) {}

  ngOnInit() {
    this.registerBuilderInitializers();
    this.store.dispatch(new OneClickLoadBusinessLinesAction());
    this.initPreloadedOrder();
    this.listenResult();
    this.handleModalBackdropClick();
    this.initBusinessLines();
    this.rtl = this.rtlDetector.activeLangRTL();
    this.canAddNewProduct = this.featureToggle.isFeatureEnabled(Features.AddNewProductOneClick);
  }

  /**
   * Registers dynamically the effects needed to begin template order builder
   */
  private registerBuilderInitializers() {
    this.dynamicEffectsService.registerEffectsByType([OneClickJobsitesEffects, OneClickPointOfDeliveriesEffects]);
  }

  private handleModalBackdropClick() {
    this.dialogRef.backdropClick().subscribe(() => {
      this.goToOneClickProfiles();
    });
  }

  /**
   * Dynamically creates component
   */
  private createComponent(component, index = 0) {
    if (this.entry) {
      const factory = this.resolver.resolveComponentFactory(component);
      this.componentRenders[index] = <any>this.entry.createComponent(factory);
      this.componentRenders[index].instance.config = this.makeConfig(index);
      this.componentRenders[index].instance.mapItem = { index };
      runJSQueue(() => this.listenToFormChanges(this.componentRenders[index]));
    }
  }

  /**
   * Retrieves needed config of given parameter
   * @param full `true` gives entire config, `false` removes jobsite and pod input
   */
  private makeConfig(index: number) {
    if (index === 0) {
      return this.config.map((field) => {
        return { ...field, at: index };
      });
    } else {
      const config = this.config.filter((field) => {
        return ![OneClickBuilderField.Jobsite, OneClickBuilderField.PointOfDelivery].includes(<any>field.name);
      });
      return config.map((field) => {
        return { ...field, at: index };
      });
    }
  }

  /**
   * Starts listening to item form changes
   * @param compontentRender
   */
  private listenToFormChanges(compontentRender: ComponentRef<ConfigurableFormComponent>) {
    compontentRender.instance.form.valueChanges
      .pipe(takeUntil(this.formRebuild), takeUntil(this.ngDestroy), debounceTime(300))
      .subscribe(() => {
        this.isFormDataValidIndicator = this.isFormDataValid(compontentRender);
      });
  }

  /**
   * Selects given business lines
   * @param businessLine
   */
  public selectBusinessLine(businessLine: BusinessLine) {
    this.store.dispatch(new OneClickProfileResetBuilderAction());
    this.store.dispatch(new OneClickSelectBusinessLinesAction(businessLine));
    this.store.dispatch(new OneClickLoadShippingMethodAction());
    this.initShippingConditions();
  }

  /**
   * Selects given shipping method
   * @param shippingMethod
   */
  public selectShippingMethod(shippingMethod: ShippingCondition) {
    this.store.dispatch(new OneClickProfileResetBuilderAction());
    this.store.dispatch(new OneClickSelectShippingMethodAction(shippingMethod));
    this.configService
      .getOneClickShippingModes()
      .pipe(take(1))
      .subscribe((shippingModes) => {
        this.store.dispatch(new OneClickLoadShippingModeCompleteAction(shippingModes));
        this.shippingLocationModes = shippingModes;
        this.initShippingLocationModes();
      });
  }

  /**
   * Selects given shipping mode
   * @param shippingMode
   */
  public selectShippingLocationMode(shippingMode: ShippingLocationMode) {
    this.selectedShippingMode = shippingMode;
    this.store.dispatch(new OneClickSelectShippingModeAction(shippingMode));
    this.configService
      .getOneClickBuilderConfig(shippingMode)
      .pipe(take(1))
      .subscribe((config) => {
        this.config = config;
        if (this.componentRenders) {
          this.componentRenders.forEach((componentRender) => {
            componentRender.destroy();
          });
        }
        this.createComponent(ConfigurableFormComponent);
      });
  }

  /**
   * Adds a new product line
   */
  public addItem() {
    this.createComponent(ConfigurableFormComponent, this.componentRenders.length);
  }

  /**
   * Removes product line
   */
  public removeItem() {
    this.componentRenders[this.componentRenders.length - 1].destroy();
    this.componentRenders.splice(this.componentRenders.length - 1, 1);
  }

  /**
   * Creates one click profile
   */
  public createProfile() {
    if (this.isAllFormDataValid()) {
      if (this.preloadedOrder) {
        this.store.dispatch(new OneClickUpdateProfileAction());
      } else {
        this.store.dispatch(new OneClickCreateProfileAction());
      }
    }
  }

  /**
   * Validates all product lines in form
   */
  private isAllFormDataValid() {
    return this.componentRenders.every((componentRender) => {
      return this.isFormDataValid(componentRender);
    });
  }

  /**
   * States whether all of the form input is valid.
   * If some control is invalid, return false
   */
  public isFormDataValid(componentRender: ComponentRef<ConfigurableFormComponent>): boolean {
    componentRender.instance.updateValueAndValidity();
    return Object.keys(componentRender.instance.form.controls).every((key) => {
      const control: AbstractControl = componentRender.instance.form.controls[key];
      control.markAsTouched();
      const invalid = control.invalid;
      const valid = control.valid;
      const validator = control.validator;
      const disabled = control.disabled;

      // Valid will be false if the form control is disabled
      return invalid === false && (valid === true || disabled === true || validator === null);
    });
  }

  /**
   * Cancels profile creations
   */
  public cancel() {
    if (this.profileState && this.profileState.result) {
      this.goToOneClickProfiles();
      return;
    }
    this.store.dispatch(new OneClickProfileResetAction());
    this.router.navigate([], { queryParams: { oneclick: null }, queryParamsHandling: 'merge' });
    this.dialogRef.close(null);
    this.ngDestroy.next();
  }

  /**
   * Reload One Click Profiles table and closes the modal
   */
  public goToOneClickProfiles() {
    this.store.dispatch(new OneClickProfileResetAction());
    this.router.navigate(['app/one-click/profiles']);
    this.dialogRef.close(null);
    this.store.pipe(select(getLegalEntity)).subscribe((customer: ILegalEntity) => {
      if (customer) {
        this.store.dispatch(
          new OneClickProfilesLoad({
            customerId: customer.legalEntityId,
            fetch: 10,
            page: 1,
          }),
        );
      }
    });
  }

  /**
   * Initializes preloaded order if any
   */
  private initPreloadedOrder() {
    this.store
      .pipe(
        select(getOneClickPreloadedOrder),
        filter((order) => !!order),
        take(1),
      )
      .subscribe((preloaded) => (this.preloadedOrder = preloaded));

    this.store
      .select(getOneClickPreloadedOrderLoading)
      .pipe(takeUntil(this.ngDestroy))
      .subscribe((isLoading) => (this.isLoadingPreloadedOrder = isLoading));

    this.store
      .select(getOneClickBuilderEditMode)
      .pipe(takeUntil(this.ngDestroy))
      .subscribe((isEditMode) => (this.isEditMode = isEditMode));
  }

  /**
   * Listens to profile creation result
   */
  private listenResult() {
    this.store.pipe(select(getOneClickProfileState)).subscribe((state) => {
      this.profileState = state;
    });
    this.store.pipe(select(getOneClickStdLot)).subscribe((state) => {
      this.stdLoading = state.loading;
    });
  }

  /**
   * Initialize business lines
   */
  private initBusinessLines() {
    this.store
      .pipe(
        select(getOneClickBusinessLines),
        switchMap((businessLines) =>
          this.store.select(getOneClickPreloadedOrder).pipe(
            take(1),
            map((preloadedOrder) => ({ businessLines, preloadedOrder })),
          ),
        ),
        // Radio button component autoselects the first item so
        map(({ businessLines, preloadedOrder }) => {
          if (businessLines.length) {
            this.selectBusinessLine(
              preloadedOrder
                ? businessLines.find(
                    (businessLine) =>
                      businessLine.businessLineCode === preloadedOrder.items[0].product.productType.productTypeCode,
                  )
                : businessLines[0],
            );
          }
          return businessLines;
        }),
        switchMap((businessLines) =>
          this.store.select(getOneClickIsSelectedOrderFromTable).pipe(
            take(1),
            map(({ canUseBusinessline }) => ({ businessLines, canUseBusinessline })),
          ),
        ),
        takeUntil(this.ngDestroy),
      )
      .subscribe(({ businessLines, canUseBusinessline }) => {
        this.businessLines = businessLines;
        if (!!canUseBusinessline) {
          this.preSelectedBusinessLine = canUseBusinessline as BusinessLine;
          this.selectBusinessLine(canUseBusinessline as BusinessLine);
        }
      });
  }

  /**
   * Initialize shipping conditions (Delivery, Pickup, etc...)
   */
  private initShippingConditions() {
    this.store
      .pipe(
        select(getOneClickShippingMethods),
        switchMap((methods) =>
          this.store.select(getOneClickPreloadedOrder).pipe(
            take(1),
            map((preloadedOrder) => ({ methods, preloadedOrder })),
          ),
        ),
        // Radio button component autoselects the first item so
        map(({ methods, preloadedOrder }) => {
          if (methods.length) {
            if (preloadedOrder) {
              // If there is a preloaded order, pre select
              this.selectShippingMethod(
                methods.find(
                  (method) => method.shippingConditionCode === preloadedOrder.shippingCondition.shippingConditionCode,
                ),
              );
            } else {
              // Select last option if not preloaded
              const first = methods.length && methods[0];
              this.selectShippingMethod(first);
            }
          }
          return methods;
        }),
        takeUntil(this.ngDestroy),
      )
      .subscribe((methods) => {
        this.shippingMethods = methods;
      });
  }

  /**
   * Initialize shipping location modes (Jobsite, POD)
   */
  private initShippingLocationModes() {
    this.store
      .select(getOneClickPreloadedOrder)
      .pipe(take(1))
      .subscribe((preloadedOrder) => {
        if (preloadedOrder) {
          // If order preloaded
          if (isJobsite(preloadedOrder)) {
            // Preseclt jobsite mode
            const jobsiteMode = this.shippingLocationModes.find(
              (mode) => mode.code === ShippingLocationModeCode.Jobsite,
            );
            this.selectShippingLocationMode(jobsiteMode);
          } else if (isPointOfDelivery(preloadedOrder)) {
            // Preselct point of delivery mode
            const podMode = this.shippingLocationModes.find(
              (mode) => mode.code === ShippingLocationModeCode.PointOfDelivery,
            );
            this.selectShippingLocationMode(podMode);
          }
        } else {
          // Not preloaded, select first option avialable
          const first = this.shippingLocationModes[0];
          this.selectShippingLocationMode(first);
        }
      });
  }

  ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
    this.formRebuild.next();
    this.formRebuild.complete();
  }
}
